import { useState } from 'react';

import { getHTMLInputType } from './TextFieldAtom.helpers';
import {
    ErrorText,
    InputWrapper,
    Label,
    LabelWrapper,
    RequiredSpan,
    ShowHideIcon,
    StyledLabel,
    TextInput,
} from './TextFieldAtom.styled';

export interface DumbTextFieldAtomI {
    /** Indicates if an error exists for this field */
    hasError: boolean;

    /** The key from the registration config, to be used as name/id of the field */
    id?: string;

    /** Display label for the field */
    label: string;

    /** What to show when nothing is entered into the field */
    placeholder: string;

    /** Whether the field is required to continue */
    required: boolean;

    /** The type of HTML text input */
    type: string;

    /** Callback to trigger to update the value managed externally */
    updateField: (value: string) => void;

    /** The current value of the field, managed by the form */
    value: string;

    /** Puts the input field in focus if true */
    autoFocus?: boolean;

    /** Controls display of the eye icon button, used to toggle password visibility */
    /** Works together with the field.type, currently only for `text` | `password` */
    canToggleTypeAttribute?: boolean;

    /** When set to true will stops browsers from auto completing the input field */
    disableAutoComplete?: boolean;

    /** Error message to be displayed below each field */
    errorText?: string;
}

const DumbTextFieldAtom = ({
    hasError,
    id,
    label,
    placeholder,
    required,
    type,
    updateField,
    value,
    autoFocus,
    canToggleTypeAttribute,
    disableAutoComplete,
    errorText,
}: DumbTextFieldAtomI) => {
    const [inputType, setInputType] = useState(getHTMLInputType(type));
    const toggleType = () => {
        setInputType((val) => (val === 'password' ? 'text' : 'password'));
    };

    return (
        <StyledLabel hasError={hasError} htmlFor={id}>
            <LabelWrapper>
                <Label>
                    {label} {required && <RequiredSpan>*</RequiredSpan>}
                </Label>
                {hasError && errorText && <ErrorText>{errorText}</ErrorText>}
            </LabelWrapper>
            <InputWrapper>
                <TextInput
                    type={inputType}
                    placeholder={placeholder}
                    id={id}
                    name={id}
                    autoFocus={autoFocus}
                    value={value}
                    onChange={(e) => updateField(e.currentTarget.value)}
                    canToggleTypeAttribute={canToggleTypeAttribute}
                    autoComplete={disableAutoComplete ? 'new-password' : ''}
                />
                {(inputType === 'password' || inputType === 'text') &&
                    canToggleTypeAttribute && (
                        <ShowHideIcon
                            className={`fa fa-${
                                inputType === 'password' ? 'hide' : 'show'
                            }`}
                            onClick={toggleType}
                        />
                    )}
            </InputWrapper>
        </StyledLabel>
    );
};

export default DumbTextFieldAtom;
